(ns app.front.view.group.admin.users.subs
  "Data and commands of the group user management view: the group's
  user-groups (`:group/get-user-groups`), the qualifications under its
  inspectors group, its users (`:group/get-users`), and for an external module
  the user-groups not yet pushed to its system. Editing state lives in `app.front.view.user-management.state` under
  the scope `[:group group-id]`."
  (:require [app.domain.group.model :as group-model]
            [app.domain.util :as du]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.user-management.model :as model]
            [app.front.view.user-management.state :as state]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn scope [group-id]
  [:group group-id])

;; ---------------------------------------------------------------------------
;; Fetching
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::fetch
  ;; Data from an earlier visit would render the page with stale groups.
  (fn [{:keys [db]} [_ group-id]]
    {:db (update db ::data dissoc group-id)
     :fx [[:dispatch [::fetch-user-groups group-id]]
          [:dispatch [::fetch-users group-id]]
          ;; Empty unless the group is an external module.
          [:dispatch [::fetch-sync-status group-id]]]}))

(rf/reg-event-fx ::fetch-sync-status
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/external-sync-status
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-sync-status.success group-id]}}))

(rf/reg-event-db ::fetch-sync-status.success
  (fn [db [_ group-id _status sync-status]]
    (assoc-in db [::data group-id :sync-status] sync-status)))

(defn- sync-status-check
  "In an external module a change is pushed after it is saved, normally within
  a moment; the status is fetched again once the push has had time to land."
  [db group-id]
  (when (get-in db [::data group-id :sync-status :module-name])
    [:dispatch-later {:ms 5000 :dispatch [::fetch-sync-status group-id]}]))

(rf/reg-event-fx ::fetch-user-groups
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/get-user-groups
                    :method     :get
                    :data       {:parent-group-id group-id}
                    :on-success [::fetch-user-groups.success group-id]}}))

(rf/reg-event-db ::fetch-user-groups.success
  (fn [db [_ group-id _status user-groups]]
    (update-in db [::data group-id] assoc
               :user-groups user-groups
               ;; Qualifications are the inspectors group's own user-groups.
               :qualifications (-> user-groups
                                   (p/find-first (partial group-model/has-role? :group.role/inspector))
                                   :qualifications))))

(rf/reg-event-fx ::fetch-users
  (fn [{:keys [db]} [_ group-id]]
    {:db           (assoc-in db [::data group-id :loading?] true)
     :comm/command {:command          :group/get-users
                    :method           :get
                    :data             {:parent-group-id group-id}
                    :on-success       [::fetch-users.success group-id]
                    :finally-dispatch [::fetch-users.done group-id]}}))

(defn- with-group-ids
  "The shared components expect `:user/group-ids`, as the tenant users have."
  [user]
  (assoc user :user/group-ids (set (map :db/id (:user/groups user)))))

(rf/reg-event-db ::fetch-users.success
  (fn [db [_ group-id _status users]]
    (assoc-in db [::data group-id :users] (mapv with-group-ids users))))

(rf/reg-event-db ::fetch-users.done
  (fn [db [_ group-id]]
    (assoc-in db [::data group-id :loading?] false)))

;; ---------------------------------------------------------------------------
;; Groups
;; ---------------------------------------------------------------------------

(rf/reg-sub ::data
  (fn [db [_ group-id]]
    (get-in db [::data group-id])))

(rf/reg-sub ::loading?
  (fn [[_ group-id]] (rf/subscribe [::data group-id]))
  :-> :loading?)

(rf/reg-sub ::groups-loaded?
  ;; The page and the add forms are built from the user-groups.
  (fn [[_ group-id]] (rf/subscribe [::data group-id]))
  :-> (comp some? :user-groups))

(rf/reg-sub ::user-groups
  ;; Meeting coordinators only exist with domain-level meetings.
  (fn [[_ group-id]]
    [(rf/subscribe [::data group-id])
     (rf/subscribe [::ff/disabled? :feature-flag.id/domain-level-meetings])])
  (fn [[data meetings-disabled?] _]
    (->> (:user-groups data)
         (remove #(and meetings-disabled?
                       (group-model/has-role? :group.role/meeting-coordinator %))))))

(rf/reg-sub ::sync-pending
  ;; `{:module-name "PWAM" :group-names ["Technician"]}` while some of an
  ;; external module's user-groups are not yet pushed to its system.
  (fn [[_ group-id]]
    [(rf/subscribe [::data group-id])
     (rf/subscribe [::user-groups group-id])])
  (fn [[{{:keys [module-name pending-group-ids]} :sync-status} user-groups] _]
    (when (seq pending-group-ids)
      {:module-name module-name
       :group-names (->> user-groups
                         (filter (comp (set pending-group-ids) :db/id))
                         (map :group/name))})))

(rf/reg-sub ::qualifications
  (fn [[_ group-id]] (rf/subscribe [::data group-id]))
  :-> (comp (partial sort-by :group/name) :qualifications))

(rf/reg-sub ::inspectors-group-id
  (fn [[_ group-id]] (rf/subscribe [::user-groups group-id]))
  (fn [groups _]
    (:db/id (p/find-first groups (partial group-model/has-role? :group.role/inspector)))))

;; The group's own admin group: an admin cannot leave it from here.
(rf/reg-sub ::admin-group-ids
  (fn [[_ group-id]] (rf/subscribe [::user-groups group-id]))
  (fn [groups _]
    (->> (filter :admin-group? groups)
         du/entity-id-set)))

(rf/reg-sub ::approver-group?
  (fn [[_ group-id]] (rf/subscribe [::user-groups group-id]))
  (fn [groups _]
    (some (partial group-model/has-role? :group.role/approver) groups)))

(rf/reg-sub ::email-recipients-group
  (fn [[_ group-id]] (rf/subscribe [::user-groups group-id]))
  (fn [groups _]
    (p/find-first groups (partial group-model/has-role? :group.role/email-recipient))))

(rf/reg-sub ::editable-ids
  ;; The user-groups and qualifications a draft covers.
  (fn [[_ group-id]]
    [(rf/subscribe [::user-groups group-id])
     (rf/subscribe [::qualifications group-id])])
  (fn [[groups qualifications] _]
    (set (map :db/id (concat groups qualifications)))))

(defn toggle-group
  "`model/toggle-group`, plus qualifications go with the inspectors group."
  [{:keys [inspectors-group-id qualification-ids] :as context} group-ids group-id checked?]
  (let [ids (if (contains? qualification-ids group-id)
              ((if checked? conj disj) group-ids group-id)
              (model/toggle-group context group-ids group-id checked?))]
    (cond-> ids
      (not (contains? ids inspectors-group-id)) (#(apply disj % qualification-ids)))))

;; ---------------------------------------------------------------------------
;; Users
;; ---------------------------------------------------------------------------

(rf/reg-sub ::users
  (fn [[_ group-id]] (rf/subscribe [::data group-id]))
  :-> :users)

(rf/reg-sub ::member-counts
  ;; How many users each user-group has: a group a configuration relies on
  ;; keeps its last member.
  (fn [[_ group-id]] (rf/subscribe [::users group-id]))
  (fn [users _]
    (frequencies (mapcat :user/group-ids users))))

(rf/reg-sub ::group-names-by-id
  (fn [[_ group-id]] (rf/subscribe [::user-groups group-id]))
  (fn [groups _]
    (p/map-vals :group/name (p/index-by :db/id groups))))

(defn- matching [users group-names-by-id query]
  (let [q (model/normalize-query query)]
    (filter #(model/matches? q (keep group-names-by-id (:user/group-ids %)) %) users)))

(rf/reg-sub ::persons
  (fn [[_ group-id]]
    [(rf/subscribe [::users group-id])
     (rf/subscribe [::group-names-by-id group-id])
     (rf/subscribe [::state/query (scope group-id)])])
  (fn [[users names query] _]
    (matching (remove model/service-account? users) names query)))

(rf/reg-sub ::service-accounts
  (fn [[_ group-id]]
    [(rf/subscribe [::users group-id])
     (rf/subscribe [::group-names-by-id group-id])
     (rf/subscribe [::state/query (scope group-id)])])
  (fn [[users names query] _]
    (matching (filter model/service-account? users) names query)))

;; ---------------------------------------------------------------------------
;; Writes
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::save-user
  (fn [_ [_ group-id user-id {:keys [add remove first-name last-name]}]]
    {:comm/command {:command          :group/edit-user-groups
                    :data             (p/assoc-if {:user-id user-id
                                                   :add     (vec add)
                                                   :remove  (vec remove)}
                                                  :first-name first-name
                                                  :last-name last-name)
                    :notify           {:success :changes-saved}
                    :on-success       [::saved group-id user-id]
                    :finally-dispatch [::fetch-users group-id]}}))

(rf/reg-event-fx ::saved
  (fn [{:keys [db]} [_ group-id user-id]]
    {:fx (cond-> [[:dispatch [::state/discard-draft (scope group-id) user-id]]
                  (sync-status-check db group-id)]
           ;; An admin's own access may have changed.
           (= user-id (-> db :session/user :db/id))
           (conj [:dispatch [:core.front.session/restore-session]]))}))

(rf/reg-event-fx ::remove-user
  (fn [_ [_ group-id user-id]]
    {:comm/command {:command          :group/remove-user
                    :data             {:user/id user-id :parent-group-id group-id}
                    :notify           {:success :user-management/removed}
                    :on-success       [::removed group-id user-id]
                    :finally-dispatch [::fetch-users group-id]}}))

(rf/reg-event-fx ::removed
  (fn [{:keys [db]} [_ group-id user-id]]
    {:fx [[:dispatch [::state/discard-draft (scope group-id) user-id]]
          (sync-status-check db group-id)]}))

(rf/reg-event-fx ::added
  (fn [{:keys [db]} [_ group-id]]
    {:fx [[:dispatch [::fetch-users group-id]]
          (sync-status-check db group-id)]}))

(rf/reg-event-fx ::sync-retried
  (fn [{:keys [db]} [_ group-id]]
    {:fx [(sync-status-check db group-id)]}))

(rf/reg-event-fx ::retry-sync
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/retry-external-sync
                    :data       {:group-id group-id}
                    :on-success [::sync-retried group-id]}}))

(defn- update-user [db group-id user-id f & args]
  (update-in db [::data group-id :users]
             (partial mapv #(if (= user-id (:db/id %)) (apply f % args) %))))

;; The new key's secret is only ever returned here; the user's API key table
;; shows it until the users are fetched again.
(rf/reg-event-fx ::create-api-key
  (fn [_ [_ group-id user-id]]
    {:comm/command {:command    :group/create-service-account-api-key
                    :data       {:user-id user-id :group-id group-id}
                    :notify     {:success :api-key-ui/add-new-success}
                    :on-success [::create-api-key.success group-id user-id]}}))

(rf/reg-event-db ::create-api-key.success
  (fn [db [_ group-id user-id _status api-key]]
    (update-user db group-id user-id update :user/api-keys (fnil conj []) api-key)))

(rf/reg-event-db ::api-key-updated
  (fn [db [_ group-id user-id _status {:db/keys [id] :as api-key}]]
    (update-user db group-id user-id update :user/api-keys
                 (partial mapv #(if (= id (:db/id %)) (merge % api-key) %)))))

(rf/reg-event-db ::api-key-deleted
  (fn [db [_ group-id user-id key-id]]
    (update-user db group-id user-id update :user/api-keys
                 (partial filterv #(not= key-id (:db/id %))))))

(rf/reg-event-fx ::set-user-mfa-status
  ;; MFA is not a membership, so it is set at once rather than with Save.
  (fn [_ [_ group-id params]]
    {:comm/command {:command    :group/set-user-mfa-status
                    :data       params
                    :notify     {:success :changes-saved}
                    :on-success [::fetch-users group-id]}}))

;; ---------------------------------------------------------------------------
;; Email recipients
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::edit-emails
  (fn [_ [_ group-id email-group-id op emails]]
    {:comm/command {:command          :group/edit-emails
                    :data             {:group-id email-group-id op (vec emails)}
                    :notify           {:success :changes-saved}
                    :on-success       [::close-emails-form group-id]
                    :finally-dispatch [::fetch-user-groups group-id]}}))

(rf/reg-event-db ::open-emails-form
  (fn [db [_ group-id]]
    (assoc-in db [::emails-form group-id] "")))

(rf/reg-event-db ::set-emails-text
  (fn [db [_ group-id text]]
    (assoc-in db [::emails-form group-id] text)))

(rf/reg-event-db ::close-emails-form
  (fn [db [_ group-id]]
    (update db ::emails-form dissoc group-id)))

(rf/reg-sub ::emails-form
  (fn [db [_ group-id]]
    (get-in db [::emails-form group-id])))

;; ---------------------------------------------------------------------------
;; Navigation
;; ---------------------------------------------------------------------------

(rf/reg-sub ::current-approver-group?
  ;; For the route's `:view-sub`, which takes no group id.
  (fn [db _]
    (->> (get-in db [::data (::gas/group-id db) :user-groups])
         (some (partial group-model/has-role? :group.role/approver)))))

(rf/reg-sub ::view-data
  ;; Managing one of the domain's approver groups returns to them.
  :<- [::current-approver-group?]
  :<- [::gas/group]
  :<- [:branding/as-string]
  (fn [[approver? group branding] _]
    {:back-href (if approver?
                  (href/branded-href branding
                                     :view/group.admin.approver-groups-domain
                                     {:group-id (-> group :entity/parent :db/id)})
                  (href/branded-href branding :dashboard))}))
